import { useMemo, useState } from "react";
import { Calculator, MessageCircle, Info, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { waLink } from "@/data/site";
import { Reveal, SectionKicker } from "./Reveal";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const QuoteEstimator = () => {
  const { t } = useLang();
  const e = t.estimator;

  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [typeKey, setTypeKey] = useState("");
  const [sunroof, setSunroof] = useState(null); // true | false | null

  const selectedType = e?.types?.find((x) => x.key === typeKey);

  const price = useMemo(() => {
    if (!selectedType || sunroof === null) return null;
    return sunroof ? selectedType.withSun : selectedType.noSun;
  }, [selectedType, sunroof]);

  if (!e) return null;

  const waHref = () => {
    const msg = e.waTemplate({
      type: selectedType?.label || "",
      model: model.trim(),
      year: year.trim(),
      sunroof: sunroof ? e.yes : e.no,
      price,
    });
    return waLink(msg);
  };

  return (
    <section
      id="cotizador"
      data-testid="estimator-section"
      className="py-24 sm:py-32 bg-[#0a0a0a] border-y border-white/5"
    >
      <div className="container-x">
        <Reveal className="max-w-2xl">
          <SectionKicker>{e.kicker}</SectionKicker>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {e.title}
          </h2>
          <p className="mt-4 text-base text-zinc-400 leading-relaxed">
            {e.subtitle}
          </p>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-[1.1fr_0.9fr] gap-6">
          {/* Form */}
          <Reveal>
            <div className="rounded-3xl border border-white/10 bg-[#0d0d0d] p-6 sm:p-8 space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-zinc-300">
                    {e.modelLabel}
                  </label>
                  <Input
                    value={model}
                    onChange={(ev) => setModel(ev.target.value)}
                    placeholder={e.modelPlaceholder}
                    data-testid="estimator-model"
                    className="mt-2 h-12 bg-white/5 border-white/15 text-white placeholder:text-zinc-500 focus-visible:ring-brand"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-300">
                    {e.yearLabel}
                  </label>
                  <Input
                    value={year}
                    onChange={(ev) => setYear(ev.target.value)}
                    placeholder={e.yearPlaceholder}
                    inputMode="numeric"
                    data-testid="estimator-year"
                    className="mt-2 h-12 bg-white/5 border-white/15 text-white placeholder:text-zinc-500 focus-visible:ring-brand"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-zinc-300">
                  {e.typeLabel}
                </label>
                <Select value={typeKey} onValueChange={setTypeKey}>
                  <SelectTrigger
                    data-testid="estimator-type"
                    className="mt-2 h-12 bg-white/5 border-white/15 text-white focus:ring-brand"
                  >
                    <SelectValue placeholder={e.typePlaceholder} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10 text-white">
                    {e.types.map((tp) => (
                      <SelectItem
                        key={tp.key}
                        value={tp.key}
                        data-testid={`estimator-type-${tp.key}`}
                        className="focus:bg-brand/20 focus:text-white"
                      >
                        <span className="font-medium">{tp.label}</span>
                        <span className="text-zinc-400 text-xs ml-2">
                          ({tp.examples})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-zinc-300">
                  {e.sunroofLabel}
                </label>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  {[
                    { v: true, label: e.yes, testid: "estimator-sunroof-yes" },
                    { v: false, label: e.no, testid: "estimator-sunroof-no" },
                  ].map((opt) => (
                    <button
                      key={String(opt.v)}
                      type="button"
                      data-testid={opt.testid}
                      onClick={() => setSunroof(opt.v)}
                      className={`h-12 rounded-xl border font-medium transition-all flex items-center justify-center gap-2 ${
                        sunroof === opt.v
                          ? "border-brand bg-brand/15 text-white"
                          : "border-white/15 bg-white/5 text-zinc-300 hover:border-white/30"
                      }`}
                    >
                      {sunroof === opt.v && <Check className="h-4 w-4 text-brand" />}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          {/* Result */}
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl border border-brand/30 bg-gradient-to-br from-brand/15 via-[#0d0d0d] to-[#0d0d0d] p-6 sm:p-8 flex flex-col">
              <div className="flex items-center gap-2 text-brand-light text-sm font-semibold">
                <Calculator className="h-5 w-5" />
                {e.resultLabel}
              </div>

              {price !== null ? (
                <div data-testid="estimator-result" className="mt-4">
                  <div className="font-heading text-5xl sm:text-6xl font-extrabold text-white">
                    ${price}
                  </div>
                  <div className="mt-2 text-sm text-zinc-300">
                    {selectedType.label}
                    {model ? ` · ${model}` : ""}
                    {year ? ` · ${year}` : ""} ·{" "}
                    {sunroof ? `Sunroof: ${e.yes}` : `Sunroof: ${e.no}`}
                  </div>
                  <a
                    href={waHref()}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="estimator-whatsapp-btn"
                    className="mt-6 inline-flex items-center justify-center gap-2 h-13 min-h-[52px] w-full rounded-full bg-[#25D366] hover:bg-[#1eb857] text-white font-semibold transition-colors"
                  >
                    <MessageCircle className="h-5 w-5" />
                    {e.cta}
                  </a>
                </div>
              ) : (
                <div
                  data-testid="estimator-hint"
                  className="mt-4 flex-1 flex items-center justify-center text-center text-zinc-400 text-sm py-10"
                >
                  {e.completeHint}
                </div>
              )}

              <div className="mt-6 flex items-start gap-2 rounded-2xl bg-black/40 border border-white/10 p-4 text-xs text-zinc-400 leading-relaxed">
                <Info className="h-4 w-4 text-brand shrink-0 mt-0.5" />
                {e.disclaimer}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
